import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { formatDateMarathi, formatDateEnglish } from '../config/websiteConfig';
import './FallbackVisitorCounter.css';

// Helper function to get UTC date string (YYYY-MM-DD)
// Using UTC to match backend and VPS timezone
// Note: Function name kept as getISTDateString for backward compatibility
function getISTDateString() {
  const now = new Date();
  // Use UTC date components directly (matches backend)
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const day = String(now.getUTCDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

const FallbackVisitorCounter = () => {
  const { isMarathi } = useLanguage();
  const [visitorCount, setVisitorCount] = useState(0);
  const [dailyCount, setDailyCount] = useState(0);
  const [, setStartingPoint] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [websiteLastUpdated, setWebsiteLastUpdated] = useState(null);

  useEffect(() => {
    let intervalId = null;
    let isTabVisible = true;
    let lastActivity = Date.now();
    const INACTIVITY_THRESHOLD = 5 * 60 * 1000; // 5 minutes

    // Fetch starting point from backend or use stored fallback
    const fetchStartingPoint = async () => {
      try {
        // Try to get real data from backend
        const API_BASE = process.env.NODE_ENV === 'production' 
          ? 'https://adminconsole.mlwb.in/api' 
          : 'http://localhost:5000/api';
        const response = await fetch(`${API_BASE}/visitors/stats`);
        if (response.ok) {
          const data = await response.json();
          const realCount = data.total || 0;
          const realDaily = data.daily || 0;
          
          // ALWAYS store the real count for future fallback with UTC date
          const todayUTC = getISTDateString();
          localStorage.setItem('lastKnownVisitorCount', realCount.toString());
          localStorage.setItem('lastKnownDailyCount', realDaily.toString());
          localStorage.setItem('lastKnownDailyCountDate', todayUTC); // Store the date for validation
          localStorage.setItem('lastKnownCountTimestamp', Date.now().toString());
          
          setStartingPoint(realCount);
          setVisitorCount(realCount);
          setDailyCount(realDaily);
          setIsLoading(false);
          return;
        }
      } catch (error) {
        console.warn('Backend unavailable, using stored fallback:', error.message || 'Network error');
      }
      
      // Fallback: Use stored count if available AND it's from today (UTC)
      const lastKnownCount = localStorage.getItem('lastKnownVisitorCount');
      const lastKnownDaily = localStorage.getItem('lastKnownDailyCount');
      const lastKnownDailyDate = localStorage.getItem('lastKnownDailyCountDate');
      const todayUTC = getISTDateString();
      
      if (lastKnownCount) {
        const storedCount = parseInt(lastKnownCount);
        // Only use stored daily count if it's from today (UTC)
        // If date doesn't match, use 0 for new day
        const storedDaily = (lastKnownDailyDate === todayUTC) 
          ? parseInt(lastKnownDaily, 10) || 0 
          : 0;
        setStartingPoint(storedCount);
        setVisitorCount(storedCount);
        setDailyCount(storedDaily);
      } else {
        // Only if localStorage is completely empty (first visit ever)
        console.warn('No stored count found - this should not happen in production');
        setStartingPoint(0);
        setVisitorCount(0);
        setDailyCount(0);
      }
      setIsLoading(false);
    };

    const startAutoRefresh = () => {
      if (intervalId) clearInterval(intervalId);
      intervalId = setInterval(() => {
        const now = Date.now();
        const timeSinceLastActivity = now - lastActivity;
        
        // Only refresh if tab is visible and user has been active recently
        if (isTabVisible && timeSinceLastActivity < INACTIVITY_THRESHOLD) {
          fetchStartingPoint();
        }
      }, 60000); // 60 seconds
    };

    const stopAutoRefresh = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // Track user activity
    const updateActivity = () => {
      lastActivity = Date.now();
      if (!intervalId) {
        startAutoRefresh();
      }
    };

    // Handle tab visibility changes
    const handleVisibilityChange = () => {
      isTabVisible = !document.hidden;
      if (isTabVisible) {
        fetchStartingPoint(); // Refresh immediately when tab becomes visible
        startAutoRefresh();
      } else {
        stopAutoRefresh();
      }
    };

    // Initial setup
    fetchStartingPoint();

    // Start auto-refresh
    startAutoRefresh();

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('mousedown', updateActivity);
    document.addEventListener('mousemove', updateActivity);
    document.addEventListener('keypress', updateActivity);
    document.addEventListener('scroll', updateActivity);
    document.addEventListener('touchstart', updateActivity);

    // Cleanup
    return () => {
      stopAutoRefresh();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('mousedown', updateActivity);
      document.removeEventListener('mousemove', updateActivity);
      document.removeEventListener('keypress', updateActivity);
      document.removeEventListener('scroll', updateActivity);
      document.removeEventListener('touchstart', updateActivity);
    };
  }, []);

  // Fetch website last updated date
  useEffect(() => {
    const fetchWebsiteLastUpdated = async () => {
      try {
        const API_BASE = process.env.NODE_ENV === 'production' 
          ? 'https://adminconsole.mlwb.in/api' 
          : 'http://localhost:5000/api';
        // Add timestamp to prevent caching
        const timestamp = new Date().getTime();
        const response = await fetch(`${API_BASE}/website-settings?t=${timestamp}`, { 
          cache: 'no-store'
        });
        if (response.ok) {
          const data = await response.json();
          if (data.lastUpdatedDate) {
            const dateValue = new Date(data.lastUpdatedDate);
            setWebsiteLastUpdated(dateValue);
            // Store in localStorage as fallback for next page load
            localStorage.setItem('lastKnownWebsiteLastUpdated', data.lastUpdatedDate);
            localStorage.setItem('lastKnownWebsiteLastUpdatedTimestamp', Date.now().toString());
          } else {
            // Use fallback from localStorage if API returns null
            const storedDate = localStorage.getItem('lastKnownWebsiteLastUpdated');
            if (storedDate) {
              setWebsiteLastUpdated(new Date(storedDate));
            } else {
              setWebsiteLastUpdated(null);
            }
          }
        } else {
          // Use fallback from localStorage on API error
          const storedDate = localStorage.getItem('lastKnownWebsiteLastUpdated');
          if (storedDate) {
            setWebsiteLastUpdated(new Date(storedDate));
          } else {
            setWebsiteLastUpdated(null);
          }
        }
      } catch (error) {
        console.warn('Error fetching website last updated date:', error);
        // Use fallback from localStorage on network error
        const storedDate = localStorage.getItem('lastKnownWebsiteLastUpdated');
        if (storedDate) {
          setWebsiteLastUpdated(new Date(storedDate));
        } else {
          setWebsiteLastUpdated(null);
        }
      }
    };

    // Load from localStorage first for immediate display
    const storedDate = localStorage.getItem('lastKnownWebsiteLastUpdated');
    if (storedDate) {
      setWebsiteLastUpdated(new Date(storedDate));
    }
    
    // Then fetch fresh data from API (only on page load/refresh)
    fetchWebsiteLastUpdated();
  }, []);

  // Daily count is already handled in fetchStartingPoint function
  // No need for separate useEffect - it's set when API succeeds or when using localStorage fallback

  return (
    <div className="horizontal-visitor-counter fallback-counter" data-read-section="footer-visitor-count">
      <div className="visitor-stats">
        <div className="stat-item">
          <div className="stat-number">{isLoading ? '0' : visitorCount.toLocaleString()}</div>
          <div className="stat-label">{isMarathi() ? 'एकूण भेटी' : 'Total Visits'}</div>
        </div>
        <div className="stat-separator"></div>
        <div className="stat-item">
          <div className="stat-number daily">{isLoading ? '0' : dailyCount.toLocaleString()}</div>
          <div className="stat-label">{isMarathi() ? 'दैनिक भेटी' : 'Daily Visits'}</div>
        </div>
      </div>
      {websiteLastUpdated && (
        <p className="website-last-updated-text" data-read-section="footer-last-updated">
          {isMarathi() 
            ? `वेबसाइट अंतिम अपडेट: ${formatDateMarathi(websiteLastUpdated)}` 
            : `Website Last Updated: ${formatDateEnglish(websiteLastUpdated)}`
          }
        </p>
      )}
    </div>
  );
};

export default FallbackVisitorCounter;
